import { useEffect, useState, ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './NutritionSummary.css';
import '../pages/Landing.css';

/* ====================================================================
   Types
   ==================================================================== */

interface MealReview {
  meal_type: 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'other';
  content: string;
  comment: string;
  badges: string[];
}

interface NutritionalSpotlight {
  nutrient: string;
  note: string;
}

type BreakdownSignal = 'strong' | 'moderate' | 'light' | 'none' | 'some' | 'notable';

interface BreakdownDimension {
  signal: BreakdownSignal;
  note: string;
}

interface NutritionalBreakdown {
  protein?: BreakdownDimension;
  fiber?: BreakdownDimension;
  healthy_fats?: BreakdownDimension;
  refined_carbs?: BreakdownDimension;
}

interface SummaryData {
  overview: string;
  meal_reviews: MealReview[];
  highlights: string[];
  nutritional_spotlight: NutritionalSpotlight[];
  micronutrient_coverage?: string[];
  nutritional_breakdown?: NutritionalBreakdown | null;
  coverage_note?: string | null;
  suggestions: string[] | null;
  tomorrow_hook: string | null;
}

interface NutritionSummary {
  nanoid: string;
  period_type: 'daily' | 'weekly';
  period_start: string;
  period_end: string;
  generated_at: string;
  first_name: string | null;
  summary_data: SummaryData;
}

/* ====================================================================
   Helpers
   ==================================================================== */

function capitalize(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Approved badge vocabulary (mirrors the closed list in the backend prompt).
// Acts as a render-time allowlist so any stray/off-list label from the model
// never surfaces in the UI.
const APPROVED_BADGES = new Set<string>([
  // Protein / Satiety
  'high-protein', 'lean-protein', 'plant-protein', 'high-fiber', 'fiber-boost', 'blood-sugar-stable',
  // Fat Quality
  'healthy-fats', 'omega-3', 'heart-healthy',
  // Carb Profile
  'slow-carbs', 'whole-grain', 'refined-carbs', 'low-fiber',
  // Micronutrient / Functional
  'iron-rich', 'calcium-rich', 'vitamin-c', 'antioxidant-rich', 'anti-inflammatory', 'gut-friendly', 'fermented-food',
  // Processing Level
  'whole-food-win', 'lightly-processed', 'processed-element', 'ultra-processed',
  // Watch Notes
  'high-sugar', 'high-sodium', 'fried-preparation',
]);

function normalizeBadge(badge: string): string {
  return badge.trim().toLowerCase().replace(/\s+/g, '-');
}

// Keep only approved badges (normalized), de-duped, preserving order.
function approvedBadges(badges: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const b of badges) {
    const n = normalizeBadge(b);
    if (APPROVED_BADGES.has(n) && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  return out;
}

// Approved micronutrient vocabulary for the coverage pill row (vitamins &
// minerals only — mirrors the closed list in the backend prompt). Acts as a
// render-time allowlist so any off-list slug never surfaces in the UI.
const APPROVED_MICRONUTRIENTS = new Set<string>([
  // Vitamins
  'vitamin-a', 'vitamin-c', 'vitamin-d', 'vitamin-e', 'vitamin-k',
  'vitamin-b6', 'vitamin-b12', 'folate', 'thiamin', 'riboflavin',
  // Minerals
  'iron', 'magnesium', 'potassium', 'calcium', 'zinc', 'selenium',
]);

// Keep only approved micronutrient slugs (normalized), de-duped, preserving order.
function approvedMicronutrients(slugs: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of slugs) {
    const n = normalizeBadge(s);
    if (APPROVED_MICRONUTRIENTS.has(n) && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  return out;
}

function formatBadge(badge: string): string {
  return badge.trim().split(/[-\s]+/).map(capitalize).join(' ');
}

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateRange(startStr: string, endStr: string): string {
  const [sy, sm, sd] = startStr.split('-').map(Number);
  const [ey, em, ed] = endStr.split('-').map(Number);
  const startFmt = new Date(sy, sm - 1, sd).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  const endFmt = new Date(ey, em - 1, ed).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  return `${startFmt} – ${endFmt}`;
}

const MEAL_EMOJI: Record<string, string> = {
  breakfast: '☀️',
  lunch: '🥗',
  dinner: '🍽️',
  snack: '🍎',
  other: '🌿',
};

// Display labels for meal types. "snack" covers anything that isn't a main
// meal (e.g. a coffee), so it reads as "Snacks & Other".
const MEAL_LABEL: Record<string, string> = {
  snack: 'Snacks & Other',
};

function mealTypeLabel(mealType: string): string {
  return MEAL_LABEL[mealType] ?? capitalize(mealType);
}

// Ordered dimensions for the nutritional breakdown. For "good" dimensions a
// higher signal is better; refined_carbs is inverted (less is better).
const BREAKDOWN_DIMENSIONS: { key: keyof NutritionalBreakdown; label: string; inverted: boolean }[] = [
  { key: 'protein',       label: 'Protein',       inverted: false },
  { key: 'fiber',         label: 'Fiber',         inverted: false },
  { key: 'healthy_fats',  label: 'Healthy Fats',  inverted: false },
  { key: 'refined_carbs', label: 'Refined Carbs', inverted: true },
];

// Maps a signal + direction to a qualitative tone used for the colored indicator.
function signalTone(signal: BreakdownSignal, inverted: boolean): 'good' | 'mid' | 'low' {
  const strong = inverted ? signal === 'none' : signal === 'strong';
  const weak = inverted ? signal === 'notable' : signal === 'light';
  if (strong) return 'good';
  if (weak) return 'low';
  return 'mid';
}

function signalLabel(signal: BreakdownSignal): string {
  return capitalize(signal);
}

/* ====================================================================
   Shell
   ==================================================================== */

function NutritionShell({ children }: { children: ReactNode }) {
  return (
    <div className="nutrition-page">
      <PublicHeader />
      {children}
      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img footer-logo-icon-img" />
            <span>mosaime</span>
          </div>
          <div className="footer-links">
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} MOSAIME LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

/* ====================================================================
   Page
   ==================================================================== */

export function NutritionSummaryPage() {
  const { nanoid } = useParams<{ nanoid: string }>();

  const [summary, setSummary] = useState<NutritionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'not_found' | 'expired' | 'error' | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [nanoid]);

  useEffect(() => {
    if (!nanoid) {
      setError('not_found');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`/api/nutrition-summaries/${nanoid}/viewed`, { method: 'POST' }).catch(() => {});

    const load = async () => {
      try {
        const res = await fetch(`/api/nutrition-summaries/${nanoid}`);
        if (cancelled) return;
        if (res.status === 404) { setError('not_found'); setLoading(false); return; }
        if (res.status === 410) { setError('expired');   setLoading(false); return; }
        if (!res.ok) { setError('error'); setLoading(false); return; }
        const json = await res.json();
        setSummary(json.data);
        setLoading(false);
      } catch {
        if (!cancelled) { setError('error'); setLoading(false); }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [nanoid]);

  if (loading) {
    return (
      <NutritionShell>
        <div className="nutrition-loading">
          <div className="nutrition-loading-spinner" />
          <p>Loading your summary…</p>
        </div>
      </NutritionShell>
    );
  }

  if (error || !summary) {
    return (
      <NutritionShell>
        <div className="nutrition-not-found">
          <div className="nutrition-not-found-icon">🥗</div>
          <h2>Summary not available</h2>
          <p>
            {error === 'expired'
              ? 'This link has expired. Text Mosaime to get your latest nutrition summary.'
              : error === 'not_found'
              ? 'This summary is no longer available. Open Mosaime to see your latest nutrition data.'
              : "We couldn't load this summary. Please try again."}
          </p>
          <Link to="/" className="btn hero-cta">Go to Mosaime</Link>
        </div>
      </NutritionShell>
    );
  }

  const { period_type, period_start, period_end, first_name, summary_data } = summary;
  const isDaily = period_type === 'daily';
  const dateLabel = isDaily ? formatDate(period_start) : formatDateRange(period_start, period_end);
  const title = isDaily ? 'Your Daily Nutrition Summary' : 'Your Weekly Nutrition Summary';
  const winsLabel = isDaily ? "Today's Wins" : "Week's Wins";

  return (
    <NutritionShell>

      {/* Hero */}
      <header className="nutrition-hero">
        <div className="landing-container nutrition-hero-inner">
          <p className="nutrition-period-label">{isDaily ? 'Daily Summary' : 'Weekly Summary'}</p>
          <h1 className="nutrition-title">{title}</h1>
          <p className="nutrition-date">{dateLabel}</p>
          <p className="nutrition-greeting">
            {first_name
              ? `Hi ${first_name}, here's how ${isDaily ? 'today' : 'this week'} looked.`
              : `Hi! Here's how ${isDaily ? 'today' : 'this week'} looked.`}
          </p>
        </div>
      </header>

      <div className="landing-container nutrition-body">

        {/* Overview */}
        <section className="nutrition-overview">
          <p className="nutrition-overview-text">{summary_data.overview}</p>
        </section>

        {/* Coverage note — below overview */}
        {summary_data.coverage_note && (
          <div className="nutrition-unlogged-note">
            <span className="nutrition-unlogged-icon" aria-hidden="true">💬</span>
            <span>{summary_data.coverage_note}</span>
          </div>
        )}

        {/* Meal Reviews */}
        <section className="nutrition-section">
          <h2 className="nutrition-section-title">
            <span aria-hidden="true">🍽️</span> Meals
          </h2>
          {summary_data.meal_reviews.length === 0 ? (
            <p className="nutrition-empty-meals">
              No meals were logged today — tap Reply in your SMS to share what you ate!
            </p>
          ) : (
            <div className="nutrition-meal-cards">
              {summary_data.meal_reviews.map((meal, i) => {
                const badges = approvedBadges(meal.badges);
                return (
                  <div key={i} className="nutrition-meal-card">
                    <div className="nutrition-meal-header">
                      <span className="nutrition-meal-emoji" aria-hidden="true">
                        {MEAL_EMOJI[meal.meal_type] ?? '🌿'}
                      </span>
                      <span className="nutrition-meal-type">{mealTypeLabel(meal.meal_type)}</span>
                    </div>
                    <p className="nutrition-meal-content">{meal.content}</p>
                    <p className="nutrition-meal-comment">{meal.comment}</p>
                    {badges.length > 0 && (
                      <div className="nutrition-badges">
                        {badges.map((badge, j) => (
                          <span key={j} className="nutrition-badge">{formatBadge(badge)}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Highlights */}
        {summary_data.highlights.length > 0 && (
          <section className="nutrition-section">
            <h2 className="nutrition-section-title">
              <span aria-hidden="true">✨</span> {winsLabel}
            </h2>
            <ul className="nutrition-highlights-list">
              {summary_data.highlights.map((item, i) => (
                <li key={i} className="nutrition-highlight-item">
                  <span className="nutrition-highlight-check" aria-hidden="true">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Nutritional Spotlight */}
        {summary_data.nutritional_spotlight.length > 0 && (
          <section className="nutrition-section">
            <h2 className="nutrition-section-title">
              <span aria-hidden="true">🌿</span> Nutritional Highlights
            </h2>
            <div className="nutrition-spotlight-list">
              {summary_data.nutritional_spotlight.map((item, i) => (
                <div key={i} className="nutrition-spotlight-item">
                  <span className="nutrition-spotlight-nutrient">{item.nutrient}</span>
                  <span className="nutrition-spotlight-note">{item.note}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Micronutrient Coverage — badge-style pill row */}
        {(() => {
          const micros = approvedMicronutrients(summary_data.micronutrient_coverage ?? []);
          if (micros.length === 0) return null;
          return (
            <section className="nutrition-section">
              <h2 className="nutrition-section-title">
                <span aria-hidden="true">🧬</span> Micronutrient Coverage
              </h2>
              <div className="nutrition-micro-row">
                {micros.map((micro, i) => (
                  <span key={i} className="nutrition-micro-pill">{formatBadge(micro)}</span>
                ))}
              </div>
            </section>
          );
        })()}

        {/* Nutritional Breakdown */}
        {summary_data.nutritional_breakdown && BREAKDOWN_DIMENSIONS.some(d => summary_data.nutritional_breakdown![d.key]) && (
          <section className="nutrition-section">
            <h2 className="nutrition-section-title">
              <span aria-hidden="true">📊</span> Nutritional Breakdown
            </h2>
            <div className="nutrition-breakdown-list">
              {BREAKDOWN_DIMENSIONS.map(({ key, label, inverted }) => {
                const dim = summary_data.nutritional_breakdown![key];
                if (!dim) return null;
                const tone = signalTone(dim.signal, inverted);
                return (
                  <div key={key} className={`nutrition-breakdown-row tone-${tone}`}>
                    <div className="nutrition-breakdown-head">
                      <span className="nutrition-breakdown-label">{label}</span>
                      <span className={`nutrition-breakdown-signal tone-${tone}`}>{signalLabel(dim.signal)}</span>
                    </div>
                    <div className="nutrition-breakdown-bar" aria-hidden="true">
                      <span className={`nutrition-breakdown-fill tone-${tone}`} />
                    </div>
                    {dim.note && <p className="nutrition-breakdown-note">{dim.note}</p>}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Suggestions */}
        {summary_data.suggestions && summary_data.suggestions.length > 0 && (
          <section className="nutrition-section">
            <h2 className="nutrition-section-title">
              <span aria-hidden="true">💡</span> Things to Consider
            </h2>
            <ul className="nutrition-suggestions-list">
              {summary_data.suggestions.map((item, i) => (
                <li key={i} className="nutrition-suggestion-item">
                  <span className="nutrition-suggestion-marker" aria-hidden="true">→</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Tomorrow Hook */}
        {summary_data.tomorrow_hook && (
          <section className="nutrition-hook">
            <p className="nutrition-hook-text">{summary_data.tomorrow_hook}</p>
            <p className="nutrition-hook-cta">Reply to your SMS to plan tomorrow's meals</p>
          </section>
        )}

      </div>
    </NutritionShell>
  );
}

export default NutritionSummaryPage;
